import React from 'react';
import { RefreshCw, UserCheck, Search, Trash2, Edit3, X, CheckSquare, Square, Minus, Plus, Check, Crown } from 'lucide-react';
import { GuestSide } from '../../../types/invitation';
import ThemedCard from '../../themed/ThemedCard';
import GuestSideTag from '../../GuestSideTag';
import { RSVPResponseWithTable } from '../types';

interface AttendeesTabProps {
  filteredAttendees: RSVPResponseWithTable[];
  maxGuests: number | null;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  batchEditMode: boolean;
  selectedItems: Set<string>;
  toggleSelectAll: (items: RSVPResponseWithTable[]) => void;
  getSelectAllState: (items: RSVPResponseWithTable[]) => 'none' | 'all' | 'partial';
  toggleSelectItem: (id: string) => void;
  editingNameId: string | null;
  editingNameValue: string;
  setEditingNameValue: (v: string) => void;
  updatingGuestName: string | null;
  handleStartEditName: (rsvpId: string, currentName: string) => void;
  handleCancelEditName: () => void;
  handleUpdateGuestName: (rsvpId: string) => void;
  handleNameInputKeyDown: (e: React.KeyboardEvent, rsvpId: string) => void;
  updatingGuestCount: string | null;
  handleUpdateGuestCount: (rsvpId: string, currentCount: number, increment: number) => void;
  editingTableId: string | null;
  setEditingTableId: (id: string | null) => void;
  handleUpdateTable: (rsvpId: string, newTableId: number | null) => void;
  getAvailableTables: () => Array<{ id: number; name: string }>;
  handleGuestSideToggle: (rsvpId: string, currentGuestSide: GuestSide) => void;
  sendingNotificationId: string | null;
  handleSendIndividualNotification: (attendee: RSVPResponseWithTable) => void;
  deletingRSVPId: string | null;
  handleDeleteRSVP: (rsvpId: string, guestName: string) => void;
}

export const AttendeesTab: React.FC<AttendeesTabProps> = ({
  filteredAttendees,
  maxGuests,
  searchTerm,
  setSearchTerm,
  batchEditMode,
  selectedItems,
  toggleSelectAll,
  getSelectAllState,
  toggleSelectItem,
  editingNameId,
  editingNameValue,
  setEditingNameValue,
  updatingGuestName,
  handleStartEditName,
  handleCancelEditName,
  handleUpdateGuestName,
  handleNameInputKeyDown,
  updatingGuestCount,
  handleUpdateGuestCount,
  editingTableId,
  setEditingTableId,
  handleUpdateTable,
  getAvailableTables,
  handleGuestSideToggle,
  sendingNotificationId,
  handleSendIndividualNotification,
  deletingRSVPId,
  handleDeleteRSVP,
}) => {
  return (
    <ThemedCard>
      <div className="p-6 border-b border-wedding-pink/10">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-wedding-text-body" />
              <input
                type="text"
                placeholder="搜尋出席者姓名或電子郵件..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-wedding-pink/30 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-pink/50"
              />
            </div>
          </div>
          <div className="text-sm text-wedding-text-body flex items-center">
            共 {filteredAttendees.length} 位出席者
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-wedding-pink/5">
            <tr>
              {batchEditMode && (
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-12">
                  <button
                    onClick={() => toggleSelectAll(filteredAttendees)}
                    className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-wedding-pink"
                  >
                    {(() => {
                      const selectState = getSelectAllState(filteredAttendees);
                      if (selectState === 'all') return <CheckSquare className="h-4 w-4 text-wedding-pink" />;
                      if (selectState === 'partial') return <Minus className="h-4 w-4 text-wedding-pink" />;
                      return <Square className="h-4 w-4" />;
                    })()}
                  </button>
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-28">
                賓客資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-20">
                出席人數
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-28">
                所屬桌號
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-20">
                特殊需求
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-16">
                親友分類
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-16">
                通知
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-16">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-wedding-pink/10">
            {filteredAttendees.map((attendee, index) => {
              const isLocked = maxGuests !== null && index >= maxGuests;
              return (
              <tr key={attendee.id} className={`hover:bg-wedding-pink/5 ${isLocked ? 'blur-[3px] select-none pointer-events-none' : ''}`}>
                {batchEditMode && (
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleSelectItem(attendee.id)}
                      className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-wedding-pink"
                    >
                      {selectedItems.has(attendee.id) ? (
                        <CheckSquare className="h-4 w-4 text-wedding-pink" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                )}
                <td className="px-6 py-4">
                  <div>
                    {editingNameId === attendee.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingNameValue}
                          onChange={(e) => setEditingNameValue(e.target.value)}
                          onKeyDown={(e) => handleNameInputKeyDown(e, attendee.id)}
                          className="flex-1 px-2 py-1 border border-wedding-pink/30 rounded text-sm focus:outline-none focus:ring-2 focus:ring-wedding-pink/50"
                          autoFocus
                          disabled={updatingGuestName === attendee.id}
                        />
                        <button
                          onClick={() => handleUpdateGuestName(attendee.id)}
                          disabled={updatingGuestName === attendee.id || !editingNameValue.trim()}
                          className="w-6 h-6 rounded-full bg-green-100 border border-green-300 flex items-center justify-center hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="確認"
                        >
                          {updatingGuestName === attendee.id ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : (
                            <Check className="h-3 w-3 text-green-600" />
                          )}
                        </button>
                        <button
                          onClick={handleCancelEditName}
                          disabled={updatingGuestName === attendee.id}
                          className="w-6 h-6 rounded-full bg-gray-100 border border-gray-300 flex items-center justify-center hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="取消"
                        >
                          <X className="h-3 w-3 text-gray-600" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="font-medium text-wedding-text-title cursor-pointer hover:text-wedding-pink flex items-center gap-1 group"
                        onClick={() => handleStartEditName(attendee.id, attendee.guestName)}
                        title="點擊編輯姓名"
                      >
                        {attendee.guestName}
                        <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                      </div>
                    )}
                    <div className="text-sm text-wedding-text-body">{attendee.guestEmail}</div>
                    {attendee.rsvpData.contactPhone && (
                      <div className="text-sm text-wedding-text-body">{attendee.rsvpData.contactPhone}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 min-w-[60px] justify-center">
                      {updatingGuestCount === attendee.id ? '...' : `${attendee.rsvpData.guestCount} 人`}
                    </span>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleUpdateGuestCount(attendee.id, attendee.rsvpData.guestCount, 1)}
                        disabled={updatingGuestCount === attendee.id || attendee.rsvpData.guestCount >= 10}
                        className="w-5 h-5 rounded-sm border border-wedding-pink/30 flex items-center justify-center hover:bg-wedding-pink/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="增加人數"
                      >
                        <Plus className="h-2.5 w-2.5" />
                      </button>
                      <button
                        onClick={() => handleUpdateGuestCount(attendee.id, attendee.rsvpData.guestCount, -1)}
                        disabled={updatingGuestCount === attendee.id || attendee.rsvpData.guestCount <= 1}
                        className="w-5 h-5 rounded-sm border border-wedding-pink/30 flex items-center justify-center hover:bg-wedding-pink/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="減少人數"
                      >
                        <Minus className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {editingTableId === attendee.id ? (
                    <div className="flex items-center space-x-2">
                      <select
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                        defaultValue={attendee.tableId || ''}
                        onChange={(e) => {
                          const newTableId = e.target.value ? parseInt(e.target.value) : null;
                          handleUpdateTable(attendee.id, newTableId);
                        }}
                      >
                        <option value="">未分配</option>
                        {getAvailableTables().map((table) => (
                          <option key={table.id} value={table.id}>
                            {table.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => setEditingTableId(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        attendee.tableName === '未分配'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {attendee.tableName}
                      </span>
                      <button
                        onClick={() => setEditingTableId(attendee.id)}
                        className="text-gray-400 hover:text-blue-600 ml-2"
                        title="編輯桌號"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {attendee.rsvpData.dietaryRestrictions && attendee.rsvpData.dietaryRestrictions.length > 0 ? (
                    <div className="space-y-1">
                      {attendee.rsvpData.dietaryRestrictions.map((restriction, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 mr-1">
                          {restriction.id === 'vegetarian' ? '素食' : '兒童椅'} {restriction.count}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">無</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <GuestSideTag
                    guestSide={attendee.guestSide || 'groom'}
                    onToggle={() => handleGuestSideToggle(attendee.id, attendee.guestSide || 'groom')}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {attendee.notificationSent ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                      已通知
                    </span>
                  ) : attendee.guestEmail && attendee.tableName !== '未分配' ? (
                    <button
                      onClick={() => handleSendIndividualNotification(attendee)}
                      disabled={sendingNotificationId === attendee.id}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors disabled:opacity-50"
                    >
                      {sendingNotificationId === attendee.id ? (
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        '通知'
                      )}
                    </button>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                      {!attendee.guestEmail ? '無 Email' : '未分配'}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleDeleteRSVP(attendee.id, attendee.guestName)}
                    disabled={deletingRSVPId === attendee.id}
                    className={`text-red-600 hover:text-red-900 disabled:opacity-50 ${
                      attendee.tableId ? 'bg-yellow-50 border border-yellow-200 rounded px-2 py-1' : ''
                    }`}
                    title={attendee.tableId ? `${attendee.guestName} 已安排座位，刪除將同時清理座位安排` : '刪除 RSVP 回覆'}
                  >
                    {deletingRSVPId === attendee.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {maxGuests !== null && filteredAttendees.length > maxGuests && (
        <div className="mx-6 my-4 p-4 bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-wedding-text-title">
                還有 {filteredAttendees.length - maxGuests} 位賓客的資訊被隱藏
              </p>
              <p className="text-xs text-wedding-text-body">
                升級方案即可查看所有賓客詳細資訊並進行管理
              </p>
            </div>
          </div>
          <a
            href="/pricing"
            className="px-4 py-2 bg-rose-500 text-white text-sm font-medium rounded-lg hover:bg-rose-600 transition-colors flex-shrink-0"
          >
            升級方案
          </a>
        </div>
      )}

      {filteredAttendees.length === 0 && (
        <div className="text-center py-12">
          <UserCheck className="h-12 w-12 text-wedding-text-body mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">沒有找到出席者</h3>
          <p className="text-gray-600">
            {searchTerm ? '調整搜尋條件以查看更多結果' : '目前還沒有人確認出席'}
          </p>
        </div>
      )}
    </ThemedCard>
  );
};
